import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import { EducationDesignPreview } from '../education/EducationDesignExperiment';
import CoursePage from './CoursePage';
import { normalizeBootcampDesign, resolveBootcampDesign } from './bootcampClarityHomeAssignment';
import CoursePageClarityHome from './CoursePageClarityHome';

import { BOOTCAMP_CLARITY_HOME_EXPERIMENT } from './coursePageExperiment';

/** Fresh assignments only. Inactive drafts preserve the existing route exactly. */
export default function BootcampClarityHomeExperiment({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const value = new URLSearchParams(location.search).get('bootcampDesign');
  const allowPreview = isPreviewExperimentOverrideHost();
  const preview = allowPreview ? normalizeBootcampDesign(value) : null;
  const automatedBrowser = allowPreview && window.navigator?.webdriver === true;
  const automationBaseline = automatedBrowser && !preview;
  const experiment = BOOTCAMP_CLARITY_HOME_EXPERIMENT;
  const trackingData = useMemo(
    () => ({
      page: location.pathname,
      surface: 'ai_trading_bootcamp_page',
      validVariants: experiment.variants,
      skipFeatureFlagEvaluation: Boolean(preview) || automationBaseline,
      forcedVariant: preview || undefined,
      trackForcedExposure: false,
      exposure_source: preview ? 'bootcamp_clarity_home_preview' : undefined,
    }),
    [location.pathname, preview, automationBaseline, experiment.variants],
  );
  const { variant, isLoading, isExperimentActive } = useABTest(
    experiment.name,
    experiment.featureFlag,
    'control',
    trackingData,
  );
  const { design: assigned, active } = resolveBootcampDesign({
    previewValue: value,
    allowPreview,
    automatedBrowser,
    variant,
    isLoading,
    isExperimentActive,
  });
  if (!assigned) return <>{children}</>;
  const experimentContext = {
    experiment_name: experiment.name,
    experiment_variant: assigned,
    feature_flag: experiment.featureFlag,
    [`$feature/${experiment.featureFlag}`]: assigned,
    is_experiment_active: Boolean(active && !preview),
  };
  return (
    <div
      className="education-design-experiment"
      data-bootcamp-experiment={experiment.featureFlag}
      data-bootcamp-variant={assigned}
    >
      <EducationDesignPreview variant="control">
        {assigned === 'test' ? (
          <CoursePageClarityHome experimentContext={experimentContext} />
        ) : (
          <CoursePage experimentContext={experimentContext} />
        )}
      </EducationDesignPreview>
    </div>
  );
}
